import React, { useCallback, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useHistoricalModal } from '../../contexts/HistoricalModalContext';
import { useContractSummaryState } from '../../hooks/useContractAnalytics';
import { getApiUrl } from '../../config/api';
import { MMEC_BRAND_COLORS } from '../../utils/mmecBrandColors';
import { glossary } from './analyticsGlossary';
import type { TrackedContract } from '../../contexts/ContractTrackingContext';
import type {
  ContractRef,
  HistoricalTapeBundle,
  MmecScore,
  WhaleSummary,
} from '../../types/contractAnalytics';

interface Props {
  contract: ContractRef;
  tracked?: TrackedContract | null;
  simpleLayout?: boolean;
  embedded?: boolean;
  onClose?: () => void;
}

function scoreColor(score: number): string {
  if (score >= 58) return 'var(--up)';
  if (score <= 42) return 'var(--down)';
  return MMEC_BRAND_COLORS.orange;
}

function fmtIv(iv: number | null | undefined): string {
  if (iv == null || !Number.isFinite(iv)) return '—';
  return `${(iv <= 1.5 ? iv * 100 : iv).toFixed(1)}%`;
}

function mergeWhaleFromTape(
  whale: WhaleSummary | undefined,
  tape: HistoricalTapeBundle | undefined,
  tracked: TrackedContract | null | undefined,
): WhaleSummary | undefined {
  const bidPrem = tracked?.accBidPremium;
  const askPrem = tracked?.accAskPremium;
  const hasTrackedFlow =
    (bidPrem != null && bidPrem > 0) || (askPrem != null && askPrem > 0);

  if (whale && whale.total_premium > 0) {
    if (hasTrackedFlow && whale.buy_premium === 0 && whale.sell_premium === 0) {
      const buy = askPrem ?? 0;
      const sell = bidPrem ?? 0;
      const total = buy + sell;
      return {
        ...whale,
        buy_premium: buy,
        sell_premium: sell,
        net_premium: buy - sell,
        total_premium: total,
        flow_bias_pct: total > 0 ? Math.round((100 * buy) / total) : whale.flow_bias_pct,
        source: 'watchlist_accumulated',
      } as WhaleSummary & { source?: string };
    }
    return whale;
  }

  if (hasTrackedFlow) {
    const buy = askPrem ?? 0;
    const sell = bidPrem ?? 0;
    const total = buy + sell;
    return {
      total_premium: total,
      buy_premium: buy,
      sell_premium: sell,
      net_premium: buy - sell,
      large_trades: whale?.large_trades ?? 0,
      max_print: whale?.max_print ?? 0,
      avg_print: whale?.avg_print ?? 0,
      sweep_count: whale?.sweep_count ?? 0,
      positioning_score: whale?.positioning_score ?? 50,
      flow_bias_pct: total > 0 ? Math.round((100 * buy) / total) : 50,
    };
  }

  if (!tape || !(tape.classified_contracts || tape.trades_count)) return whale;

  const bid = tape.bid_side_contracts ?? 0;
  const ask = tape.ask_side_contracts ?? 0;
  const between = tape.between_contracts ?? 0;
  const classified = tape.classified_contracts ?? bid + ask + between;
  if (classified <= 0) return whale;

  const buyPct = tape.buy_pct ?? (classified > 0 ? Math.round((100 * ask) / classified) : 50);
  return {
    total_premium: classified,
    buy_premium: ask,
    sell_premium: bid,
    net_premium: ask - bid,
    large_trades: whale?.large_trades ?? 0,
    max_print: whale?.max_print ?? 0,
    avg_print: whale?.avg_print ?? 0,
    sweep_count: whale?.sweep_count ?? 0,
    positioning_score: whale?.positioning_score ?? 50,
    flow_bias_pct: buyPct,
    source: 'historical_tape',
  } as WhaleSummary & { source?: string; sell_pct?: number };
}

const ContractSummary: React.FC<Props> = ({
  contract,
  tracked,
  simpleLayout = false,
  embedded = false,
  onClose,
}) => {
  const { language } = useLanguage();
  const { colors } = useTheme();
  const { token } = useAuth();
  const { openHistorical } = useHistoricalModal();
  const { data, loading, error } = useContractSummaryState(contract);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const fg = colors.text || '#e8e8e8';
  const dim = colors.textMuted || '#888';
  const pad = simpleLayout ? 14 : 20;
  const eyebrow: React.CSSProperties = {
    color: 'var(--muted-2)',
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
  };
  const num: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' };

  const toggle = (key: string) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  const explainWithAi = useCallback(async () => {
    if (!token) {
      setAiMessage(language === 'ar' ? 'سجّل الدخول لاستخدام الذكاء الاصطناعي' : 'Sign in to use AI explain');
      return;
    }
    setAiLoading(true);
    setAiMessage(null);
    try {
      const res = await fetch(getApiUrl('/api/ai/watchlist/analysis'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          watchlist: [contract.symbol],
          use_accumulated: true,
          analysis_language: language === 'ar' ? 'ar' : 'en',
          contract_context: contract,
          tracked_baseline: tracked
            ? {
                accBidPremium: tracked.accBidPremium,
                accAskPremium: tracked.accAskPremium,
                lastOI: tracked.lastOI,
                snapshotOI: tracked.snapshotOI,
              }
            : undefined,
        }),
      });
      const json = await res.json();
      const msg =
        json?.analyses?.[contract.symbol]?.ai_message ||
        json?.message ||
        (res.ok ? '' : 'AI unavailable');
      setAiMessage(msg || (language === 'ar' ? 'لا توجد إجابة' : 'No response'));
    } catch {
      setAiMessage(language === 'ar' ? 'فشل الطلب' : 'Request failed');
    } finally {
      setAiLoading(false);
    }
  }, [token, contract, language, tracked]);

  const openHistory = () => {
    openHistorical(
      {
        underlying: contract.symbol,
        strike: contract.strike,
        expiry: contract.expiry.slice(0, 10),
        optionType: contract.optionType === 'P' ? 'PUT' : 'CALL',
        source: 'premium',
      },
      simpleLayout ? { compactChrome: true } : undefined,
    );
  };

  const scoreBlock = data?.score as MmecScore | undefined;
  const histTape = data?.historical_tape;
  const apiStatus = data?.status;
  const gamma = data?.gamma as Record<string, number> | undefined;
  const rtl = data?.rtl as Record<string, unknown> | undefined;

  const status = useMemo(() => {
    if (!tracked && !apiStatus) return apiStatus;
    return {
      ...(apiStatus || {}),
      symbol: contract.symbol,
      strike: contract.strike,
      expiry: contract.expiry,
      optionType: contract.optionType,
      lastPrice: tracked?.lastPrice ?? apiStatus?.lastPrice,
      lastVolume: tracked?.lastVolume ?? apiStatus?.lastVolume,
      lastOI: tracked?.lastOI ?? apiStatus?.lastOI,
      lastDelta: tracked?.lastDelta ?? apiStatus?.lastDelta,
      lastIV: tracked?.lastIV ?? apiStatus?.lastIV,
      source: tracked?.lastSource || apiStatus?.source,
    };
  }, [tracked, apiStatus, contract]);

  const whale = useMemo(
    () => mergeWhaleFromTape(data?.whale?.summary, histTape, tracked),
    [data?.whale?.summary, histTape, tracked],
  );

  const flowBiasPct = useMemo(() => {
    if (whale?.flow_bias_pct != null && whale.flow_bias_pct !== 50) return whale.flow_bias_pct;
    if (histTape?.buy_pct != null) return histTape.buy_pct;
    return whale?.flow_bias_pct ?? null;
  }, [whale, histTape]);

  const section = (id: string, title: string, children: React.ReactNode) => (
    <div
      key={id}
      style={{
        border: '1px solid var(--hair)',
        background: 'var(--elev)',
        borderRadius: 10,
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => toggle(id)}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '10px 14px',
          background: 'var(--elev2)',
          border: 'none',
          color: fg,
          fontWeight: 600,
          fontSize: simpleLayout ? 13 : 14,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        {collapsed[id] ? '▸' : '▾'} {title}
      </button>
      {!collapsed[id] && <div style={{ padding: '12px 14px' }}>{children}</div>}
    </div>
  );

  return (
    <div
      style={{
        padding: pad,
        color: fg,
        fontFamily: 'var(--font-ui)',
        minHeight: embedded ? undefined : 200,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: simpleLayout ? 16 : 18, fontWeight: 600 }}>
            {contract.symbol} ${contract.strike} {contract.optionType === 'C' ? 'Call' : 'Put'}
          </h2>
          <div style={{ fontSize: 12, color: dim, marginTop: 4 }}>
            {contract.expiry.slice(0, 10)}
            {tracked ? (language === 'ar' ? ' · قائمة المراقبة' : ' · Watchlist') : ''}
          </div>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="mmec-btn is-ghost"
          >
            ✕
          </button>
        )}
      </div>

      {loading && (
        <div style={{ padding: 24, textAlign: 'center', color: dim, fontSize: 13 }}>
          {language === 'ar' ? 'جاري التحميل…' : 'Loading analytics…'}
        </div>
      )}
      {error && (
        <div
          style={{
            padding: '8px 12px',
            marginBottom: 12,
            background: 'var(--down-dim)',
            color: 'var(--down)',
            border: '1px solid color-mix(in srgb, var(--down) 40%, transparent)',
            borderRadius: 8,
            fontSize: 12.5,
          }}
        >
          {error}
        </div>
      )}

      {!loading && data && (
        <>
          {section(
            'glance',
            language === 'ar' ? 'نظرة سريعة' : 'At a glance',
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
              <div>
                <div style={eyebrow}>Price</div>
                <div style={num}>{status?.lastPrice != null ? `$${Number(status.lastPrice).toFixed(2)}` : '—'}</div>
              </div>
              <div>
                <div style={eyebrow}>OI / Vol</div>
                <div style={num}>
                  {status?.lastOI != null ? Number(status.lastOI).toLocaleString() : '—'} /{' '}
                  {status?.lastVolume != null ? Number(status.lastVolume).toLocaleString() : '—'}
                </div>
              </div>
              <div>
                <div style={eyebrow} title={glossary('mmecScore', language)}>
                  MMEC Score
                </div>
                <div
                  style={{
                    ...num,
                    fontSize: 22,
                    fontWeight: 600,
                    color: scoreBlock ? scoreColor(scoreBlock.score) : dim,
                  }}
                >
                  {scoreBlock?.score ?? '—'}
                  {scoreBlock?.bias && (
                    <span style={{ fontSize: 12, marginLeft: 8, color: dim }}>{scoreBlock.bias}</span>
                  )}
                </div>
              </div>
              <div>
                <div style={eyebrow}>Delta / IV</div>
                <div style={num}>
                  {status?.lastDelta != null ? status.lastDelta.toFixed(3) : '—'} / {fmtIv(status?.lastIV)}
                </div>
              </div>
            </div>,
          )}

          {histTape && (histTape.trades_count || histTape.classified_contracts) ? (
            section(
              'histTape',
              language === 'ar' ? 'ملخص السجل (Historical)' : 'Historical tape summary',
              <div style={{ fontSize: 13 }}>
                <div style={{ fontSize: 11, color: dim, marginBottom: 8 }}>
                  {language === 'ar'
                    ? 'نفس مصدر Historical.tsx — contract-tape-by-day'
                    : 'Same source as Historical.tsx — contract-tape-by-day'}
                  {histTape.underlying_used ? ` · ${histTape.underlying_used}` : ''}
                </div>
                <div
                  style={{
                    display: 'flex',
                    height: 8,
                    borderRadius: 4,
                    overflow: 'hidden',
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{ width: `${histTape.buy_pct ?? 50}%`, background: 'var(--up)' }}
                    title="At ask (buy)"
                  />
                  <div style={{ flex: 1, background: 'var(--down)' }} title="At bid (sell)" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                  <div>
                    <span style={{ color: dim }}>At ask (buy):</span>{' '}
                    <strong style={{ color: 'var(--up)' }}>
                      {(histTape.ask_side_contracts ?? 0).toLocaleString()}
                    </strong>
                    {histTape.buy_pct != null ? ` (${histTape.buy_pct}%)` : ''}
                  </div>
                  <div>
                    <span style={{ color: dim }}>At bid (sell):</span>{' '}
                    <strong style={{ color: 'var(--down)' }}>
                      {(histTape.bid_side_contracts ?? 0).toLocaleString()}
                    </strong>
                    {histTape.sell_pct != null ? ` (${histTape.sell_pct}%)` : ''}
                  </div>
                  <div>
                    <span style={{ color: dim }}>Between:</span>{' '}
                    {(histTape.between_contracts ?? 0).toLocaleString()}
                  </div>
                  <div>
                    <span style={{ color: dim }}>Trades:</span>{' '}
                    {(histTape.trades_count ?? 0).toLocaleString()} ·{' '}
                    {histTape.days_with_trades ?? 0} days
                  </div>
                </div>
                {histTape.strategy_note && (
                  <div style={{ marginTop: 8, fontSize: 11, color: dim, fontStyle: 'italic' }}>
                    {histTape.strategy_note}
                  </div>
                )}
              </div>,
            )
          ) : null}

          {whale &&
            section(
              'whale',
              language === 'ar' ? 'نشاط الحيتان' : 'Whale activity',
              <>
                <div
                  title={glossary('whalePremium', language)}
                  style={{
                    display: 'flex',
                    height: 8,
                    borderRadius: 4,
                    overflow: 'hidden',
                    marginBottom: 10,
                  }}
                >
                  <div style={{ width: `${flowBiasPct ?? 50}%`, background: 'var(--up)' }} />
                  <div style={{ flex: 1, background: 'var(--down)' }} />
                </div>
                <div style={{ fontSize: 12, display: 'flex', gap: 16, flexWrap: 'wrap', ...num }}>
                  <span>Buy ${Number(whale.buy_premium).toLocaleString()}</span>
                  <span>Sell ${Number(whale.sell_premium).toLocaleString()}</span>
                  <span>Net ${Number(whale.net_premium).toLocaleString()}</span>
                  <span>{whale.large_trades} large</span>
                </div>
                {(data.whale?.prints?.length ?? 0) > 0 && (
                  <table style={{ width: '100%', marginTop: 10, fontSize: 12, borderCollapse: 'collapse', ...num }}>
                    <thead>
                      <tr style={{ color: dim, fontSize: 11, fontWeight: 600, letterSpacing: '.03em' }}>
                        <th style={{ textAlign: 'start', padding: '6px 10px' }}>Time</th>
                        <th style={{ textAlign: 'end', padding: '6px 10px' }}>Prem</th>
                        <th style={{ textAlign: 'start', padding: '6px 10px' }}>Side</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.whale!.prints!.slice(0, 8).map((p, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--hair)' }}>
                          <td style={{ padding: '6px 10px' }}>{p.time || '—'}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'end' }}>${p.premium.toLocaleString()}</td>
                          <td style={{ padding: '6px 10px' }}>{p.side}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>,
            )}

          {section(
            'flow',
            language === 'ar' ? 'سياق التدفق' : 'Flow context',
            <div style={{ fontSize: 13 }}>
              <div title={glossary('rtl', language)}>
                RTL: {String(rtl?.direction ?? '—')}
                {rtl?.net_pct != null ? ` (${Number(rtl.net_pct).toFixed(1)}%)` : ''}
              </div>
              <div style={{ marginTop: 6 }} title={glossary('flowBias', language)}>
                Flow bias:{' '}
                {flowBiasPct != null
                  ? `${flowBiasPct}% buy`
                  : histTape?.buy_pct != null
                    ? `${histTape.buy_pct}% buy (historical tape)`
                    : '—'}
              </div>
              {tracked && (tracked.accBidPremium || tracked.accAskPremium) ? (
                <div style={{ marginTop: 6, fontSize: 11, color: dim }}>
                  Watchlist accumulated: ask ${(tracked.accAskPremium ?? 0).toLocaleString()} / bid $
                  {(tracked.accBidPremium ?? 0).toLocaleString()}
                </div>
              ) : null}
            </div>,
          )}

          {section(
            'gamma',
            language === 'ar' ? 'خريطة الجاما' : 'Gamma map',
            <div style={{ fontSize: 13 }} title={glossary('gammaFlip', language)}>
              <div>Flip: {gamma?.gamma_flip ?? '—'}</div>
              <div>Call wall: {gamma?.call_wall ?? '—'}</div>
              <div>Put wall: {gamma?.put_wall ?? '—'}</div>
              <div>Spot: {gamma?.spot ?? '—'}</div>
            </div>,
          )}

          {scoreBlock?.components &&
            section(
              'score',
              language === 'ar' ? 'تفصيل الدرجة' : 'Score breakdown',
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {scoreBlock.components.map((c) => {
                  let detail = c.detail;
                  if (c.id === 'oi_momentum' && tracked?.lastOI != null && tracked.snapshotOI != null) {
                    const dOi = tracked.lastOI - tracked.snapshotOI;
                    detail = `OI ${dOi >= 0 ? '+' : ''}${dOi.toLocaleString()} since tracked`;
                  }
                  return (
                    <div key={c.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span>{c.label}</span>
                        <span style={num}>{c.value}</span>
                      </div>
                      <div
                        style={{
                          height: 4,
                          background: 'var(--hair)',
                          borderRadius: 2,
                          marginTop: 4,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${c.value}%`,
                            height: '100%',
                            background: scoreColor(c.value),
                          }}
                        />
                      </div>
                      <div style={{ fontSize: 11, color: dim, marginTop: 2 }}>{detail}</div>
                    </div>
                  );
                })}
              </div>,
            )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <button
              type="button"
              onClick={openHistory}
              className="mmec-btn"
            >
              {language === 'ar' ? 'السجل الكامل' : 'Full history'}
            </button>
            <button
              type="button"
              onClick={explainWithAi}
              disabled={aiLoading}
              style={{
                height: 28,
                padding: '0 12px',
                borderRadius: 8,
                border: 'none',
                background: MMEC_BRAND_COLORS.orange,
                color: '#000',
                cursor: aiLoading ? 'wait' : 'pointer',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'var(--font-ui)',
                opacity: aiLoading ? 0.7 : 1,
              }}
            >
              {aiLoading
                ? language === 'ar'
                  ? 'جاري…'
                  : 'Explaining…'
                : language === 'ar'
                  ? 'شرح بالذكاء الاصطناعي'
                  : 'Explain with AI'}
            </button>
          </div>
          {aiMessage && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 8,
                background: 'rgba(244,162,97,0.12)',
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {aiMessage}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContractSummary;
